import moment from "moment";
import numeral from "numeral";
import { v4 as uuidv4 } from "uuid";
import bcrypt from "bcryptjs";
import { Application, Product } from "./constants";
import { Routes } from "../common/routing";
import { saveSessionStorage } from "./storageUtils";

export const readFromCache = (lastFetched) => {
  const diffInSeconds = moment().diff(moment(lastFetched), "seconds");
  //60 to come from configs
  if (diffInSeconds < 1) {
    return true;
  }
  return false;
};

export const formatNumber = (x) => {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const formatCurrency = (number, format = "($0a)") => {
  return numeral(number).format(format);
};

export const shortenIncome = (x) => {
  return Math.floor(x / 1000).toString() + "k";
};

export const toPascalCase = (x) => {
  return x.replace(/\w+/g, function (w) {
    return w[0].toUpperCase() + w.slice(1).toLowerCase();
  });
};

export const isEqualTo = (a, b) => {
  return (
    a.localeCompare(b, undefined, {
      sensitivity: "accent",
    }) === 0
  );
};

export const getHashcode = (initiator) => {
  var hash = 0;
  for (var i = 0; i < initiator.length; i++) {
    hash = initiator.charCodeAt(i) + ((hash << 5) - hash);
  }
  return hash;
};

/// Calculates the difference between two dates in UTC Iso format.
/// It matters for the format to follow 'yyyy-MM-ddThh:mm:ss.sssZ' otherwise complications arises
/// For the record, to get the datetime.now in Iso format use: (new Date()).toISOString()
export const getTimeDifference = ({ start, end, brief = false }) => {
  if (
    start === "0001-01-01T00:00:00.000Z" ||
    end === "0001-01-01T00:00:00.000Z"
  ) {
    return "...";
  }
  const dateTime1 = new Date(start).getTime();
  const dateTime2 = new Date(end).getTime();

  if (isNaN(dateTime1) || isNaN(dateTime2)) {
    return "";
  }

  const miliSecDiff = Math.abs(dateTime2 - dateTime1);
  const days = Math.floor(miliSecDiff / 1000 / 60 / (60 * 24));
  const date_diff = new Date(miliSecDiff);
  const hours = date_diff.getUTCHours();
  const minutes = date_diff.getUTCMinutes();
  const seconds = date_diff.getUTCSeconds();

  let result = "";

  if (days > 0) {
    result += days + "d ";
    if (brief) return result.trimEnd();
  }
  if (hours > 0) {
    result += hours + "h ";
    if (brief) return result.trimEnd();
  }
  if (minutes > 0) {
    result += minutes + "m ";
    if (brief) return result.trimEnd();
  }
  if (seconds > 0) {
    result += seconds + "s";
  }

  return result.trimEnd();
};

//Type Functions

export const getPublicImageUrl = (fileName) => {
  return getEnvironmentVariables().PUBLIC_URL + "/img/" + fileName;
};

export const removeTrailingCharacters = (value) => {
  if (value) {
    return value.replace(/,\s*$/, "");
  }
  return "";
};

//Random Functions
export const getRandomNumber = (min, max) => {
  return Math.floor(Math.random() * max) + min;
};

export const getRandomId = () => {
  return uuidv4();
};

export const getEmptyObject = (type) => {
  switch (type) {
    case "Guid":
      return "00000000-0000-0000-0000-000000000000";
  }
  switch (type) {
    case "Guid":
      return "00000000-0000-0000-0000-000000000000";
  }

  return null;
};

export const getRandomColor = (randomString) => {
  var hash = 0;
  for (var i = 0; i < randomString.length; i++) {
    hash = randomString.charCodeAt(i) + ((hash << 5) - hash);
  }
  var colour = "#";
  for (i = 0; i < 3; i++) {
    var value = (hash >> (i * 8)) & 0xff;
    colour += ("00" + value.toString(16)).substr(-2);
  }
  return colour;
};

//Cryptographyc

export const generateHash = (text) => {
  var salt = bcrypt.genSaltSync(10);
  var hash = bcrypt.hashSync(text, salt);
  return hash;
};

export const deepEqual= (obj1, obj2) => {
  // Check if both are the same reference
  if (obj1 === obj2) return true;

  // Check if either of them is not an object or is null
  if (
    typeof obj1 !== "object" ||
    obj1 === null ||
    typeof obj2 !== "object" ||
    obj2 === null
  ) {
    return false;
  }

  // Get all keys of both objects
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  // If the number of keys is different, they are not equal
  if (keys1.length !== keys2.length) {
    return false;
  }

  // Check each key in both objects
  for (let key of keys1) {
    if (!keys2.includes(key)) {
      return false; // Key not found in the second object
    }

    // Recursively check if the values are deeply equal
    if (!deepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true; // All checks passed, objects are deeply equal
}


// Returns a debounced wrapper that delays calling fn until wait milliseconds
// have passed since the last invocation. Delays live in General.Debounce.
export const debounce = (fn, wait) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
};

//Objects
export const isEmpty = (obj) => {
  if (obj === getEmptyObject("Guid")) {
    return true;
  }

  if (isArray(obj)) {
    return obj.length === 0;
  }

  let result = !obj || obj == null || Object.keys(obj).length === 0;
  return result;
};

export const isFullArray = (obj) => {
  return isArray(obj) && !isEmpty(obj);
};

export const isEmptyArray = (obj) => {
  return !isFullArray(obj);
};

export const isFull = (obj) => {
  return !isEmpty(obj);
};
function isArray(value) {
  return Array.isArray(value);
}

export const isEqualCaseInsensitive = (obj, target) => {
  return (
    obj.localeCompare(target, undefined, {
      sensitivity: "accent",
    }) === 0
  );
};

///e.g. for location: '/change-password'
export const setRedirectUrl = (location) => {
  saveSessionStorage(Application.StorageKeys.Navigation.RedirectUrl, location);
};

//Environments
export const getEnvironment = () => {
  return getEnvironmentVariables().REACT_APP_Environment;
};

export const getEnvironmentVariables = () => {
  if (!isEmpty(window._env_)) {
    return window._env_;
  }
  // Vite uses import.meta.env instead of process.env
  return typeof import.meta !== 'undefined' && import.meta.env ? import.meta.env : process.env;
};

export const getFQDN = ({
  subDomain,
  domain = `${getEnvironmentVariables().REACT_APP_HOST_ADDRESS}`,
  path,
}) => {
  if (isEmpty(subDomain)) {
    return `https://${domain}${path}`;
  }
  return `https://${subDomain}.${domain}${path}`;
};

export const redirectToDashboard = () => {
  window.location.href = `${
    getEnvironmentVariables().REACT_APP_APPLICATION_PUBLIC_URL
  }${Routes.Application.Identity.Organisation.Selection}`;
};

export const getSwagerDashboardUrl = (model) => {
  return `${getApiEndpoint(model)}/swagger`;
};

export const getApiEndpoint = (model) => {
  switch (model) {
    default:
      return Product.Names.NotConfigured;
  }
};

//Others
export const getLogo = (name) => {
  let url = "";
  switch (name) {
    case "Google":
      url =
        "https://technologyleadsstorage.blob.core.windows.net/public/ecosystem/web/others/logos/pellerex-google-logo.png";
      break;
    case "Microsoft":
      url =
        "https://technologyleadsstorage.blob.core.windows.net/public/ecosystem/web/home/technologyleads-microsoft-logo.png";
      break;
    case "Twitter":
      url =
        "https://technologyleadsstorage.blob.core.windows.net/public/ecosystem/web/home/technologyleads-twitter-logo.png";
      break;
    default:
      url = "";
      break;
  }
  return url;
};

export const scrollToTop = () => {
  document.body.scrollTo(0, 0);
};
